import { useOptionalWebSocketContext } from '@/context/websocket-context';
import { isCopilotMessageAddedMessage } from '@/hooks/use-websocket.hook';
import { getApiBase } from '@/lib/api-base';
import { fetchWebtokenHeadless } from '@/store/auth-helper';
import { useCallback, useEffect, useRef } from 'react';

/** A conversation message as the server stores it; only its id is read here. */
export type ArrivedStoredMessage = { id: string };

type Arrival = { conversationId: string; messageId: string };

type Options = {
  /** The conversation the chat has open. */
  conversationId: string | null;
  /** A turn is streaming: an arrival waits for it to end. */
  isStreaming: boolean;
  /** The message the server saved, as the server stores it. */
  onMessage: (message: ArrivedStoredMessage) => void;
};

/**
 * A message the server saved into the open conversation on its own, such as a
 * slow lookup's result, shows in the chat as it lands.
 *
 * The server sends `copilot_message_added` with the conversation and message
 * ids once the message is saved. The message itself is fetched from the saved
 * conversation, so what the chat shows is what a reload shows. It is held
 * while a turn streams, and an arrival for a conversation that is not open is
 * ignored: opening that conversation reads the saved message with the rest.
 */
export function useChatMessageArrival({
  conversationId,
  isStreaming,
  onMessage,
}: Options): void {
  const socket = useOptionalWebSocketContext();
  const subscribe = socket?.subscribe;
  const conversationRef = useRef(conversationId);
  conversationRef.current = conversationId;
  const streamingRef = useRef(isStreaming);
  streamingRef.current = isStreaming;
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const waitingRef = useRef<Arrival[]>([]);

  const fetchArrival = useCallback(async (arrival: Arrival) => {
    try {
      const token = await fetchWebtokenHeadless();
      const res = await fetch(
        `${getApiBase()}/api/v1/agent/conversations/${arrival.conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      // The chat moved on while this was fetched.
      if (conversationRef.current !== arrival.conversationId) return;
      const stored = (data?.messages ?? []).find(
        (m: ArrivedStoredMessage) => m?.id === arrival.messageId
      );
      if (stored) onMessageRef.current(stored);
    } catch {
      // The message is saved: opening the conversation shows it.
    }
  }, []);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe('chat', (message) => {
      if (!isCopilotMessageAddedMessage(message)) return;
      if (message.conversation_id !== conversationRef.current) return;
      const arrival = {
        conversationId: message.conversation_id,
        messageId: message.message_id,
      };
      if (streamingRef.current) {
        waitingRef.current.push(arrival);
        return;
      }
      void fetchArrival(arrival);
    });
  }, [subscribe, fetchArrival]);

  useEffect(() => {
    if (isStreaming || waitingRef.current.length === 0) return;
    const waiting = waitingRef.current.splice(0);
    for (const arrival of waiting) void fetchArrival(arrival);
  }, [isStreaming, fetchArrival]);
}
